import type { DashboardWidget } from "../types/core"
import {
  applyFlowPositions,
  packDashboardRows,
  rowContaining,
  rowMemberIds,
  sortWidgetsByFlowOrder,
  type FlowRow,
} from "./dashboardFlowLayout"
import {
  DASHBOARD_GRID_COLUMNS,
  getRequestedWidgetWidth,
  MAX_ROW_WIDGETS,
  snapWidgetWidth,
} from "./dashboardWidgetSizing"

/**
 * Every way the widget sequence changes: dragging a card somewhere else,
 * setting its width, cloning it, removing it. Each one re-splits whichever
 * rows its change touched, then hands the sequence back to the packer.
 */

/**
 * The width one member takes when a row splits evenly between `count` of them:
 * one card fills the row, two take a half each, three a third, four a quarter.
 * A widget too wide to accept its share keeps its own minimum, and the packer
 * moves whatever no longer fits onto the next row.
 */
function evenRowShare(widget: DashboardWidget, count: number): number {
  return snapWidgetWidth(
    widget,
    Math.floor(DASHBOARD_GRID_COLUMNS / Math.max(count, 1)),
  )
}

/** Resets the named widgets to an even split of a single row. */
function splitRowEvenly(
  widgets: DashboardWidget[],
  memberIds: string[],
): DashboardWidget[] {
  if (memberIds.length === 0) {
    return widgets
  }

  const members = new Set(memberIds)
  return widgets.map((widget) =>
    members.has(widget.id)
      ? { ...widget, grid_w: evenRowShare(widget, members.size) }
      : widget,
  )
}

/** Where a dragged widget was dropped. */
export type WidgetMoveTarget =
  /**
   * An insertion slot in a row: before its first card, between two of them, or
   * after its last. `rowAnchorId` is any current member of that row.
   */
  | { kind: "slot"; rowAnchorId: string; slotIndex: number }
  /**
   * A row of its own, opened at a boundary between existing rows.
   * `beforeWidgetId` is the first card of the row it goes above; without one
   * the new row lands below everything else.
   */
  | { kind: "newRow"; beforeWidgetId?: string }
  /**
   * Dropped straight onto another widget, taking its place in the sequence.
   * The fallback on containers too narrow to show insertion slots, where
   * `rebalanceRows` is false because the rows on screen are not the stored
   * rows and so say nothing about what the user meant.
   */
  | { kind: "widget"; overWidgetId: string; rebalanceRows: boolean }

/** Moves a widget in the sequence, re-splitting the rows its move changed. */
export function moveWidget(
  widgets: DashboardWidget[],
  activeId: string,
  target: WidgetMoveTarget,
): DashboardWidget[] {
  const ordered = sortWidgetsByFlowOrder(widgets)
  const fromIndex = ordered.findIndex((widget) => widget.id === activeId)
  if (fromIndex === -1) {
    return widgets
  }

  const rows = packDashboardRows(ordered)
  const sourceRow = rowContaining(rows, activeId)
  const placement = resolvePlacement(ordered, rows, activeId, target)
  if (!placement) {
    return widgets
  }

  const resequenced = [...ordered]
  const [moved] = resequenced.splice(fromIndex, 1)
  if (!moved) {
    return widgets
  }
  // An insertion point is an index into the sequence before the widget was
  // lifted out, so anything past the widget has since shifted a place left.
  // A widget target is not an index but a card to take the place of.
  resequenced.splice(
    placement.isInsertionPoint && placement.toIndex > fromIndex
      ? placement.toIndex - 1
      : placement.toIndex,
    0,
    moved,
  )

  const joinsOtherRow = Boolean(
    placement.joinRow && placement.joinRow !== sourceRow,
  )
  const leavesRow = joinsOtherRow || Boolean(placement.ownRow)
  const changesRowMembers =
    joinsOtherRow ||
    Boolean(placement.ownRow && (sourceRow?.widgets.length ?? 0) > 1)
  const keepsOrder = resequenced.every(
    (widget, index) => widget.id === ordered[index]?.id,
  )

  if (keepsOrder && !changesRowMembers) {
    return widgets
  }

  const vacated =
    sourceRow && leavesRow
      ? rowMemberIds(sourceRow).filter((id) => id !== activeId)
      : []

  if (placement.ownRow) {
    const widened = resequenced.map((widget) =>
      widget.id === activeId
        ? { ...widget, grid_w: DASHBOARD_GRID_COLUMNS }
        : widget,
    )
    return applyFlowPositions(splitRowEvenly(widened, vacated))
  }

  const joined =
    joinsOtherRow && placement.joinRow
      ? [...rowMemberIds(placement.joinRow), activeId]
      : []

  return applyFlowPositions(
    splitRowEvenly(splitRowEvenly(resequenced, joined), vacated),
  )
}

interface Placement {
  /** Where the widget lands: see the splice in `moveWidget` for how it reads. */
  toIndex: number
  /** True when `toIndex` is a gap in the sequence rather than a card to displace. */
  isInsertionPoint: boolean
  /** The row it joins, when the move should re-split rows. */
  joinRow?: FlowRow
  /** Set when the widget should take a full row to itself. */
  ownRow?: boolean
}

function resolvePlacement(
  ordered: DashboardWidget[],
  rows: FlowRow[],
  activeId: string,
  target: WidgetMoveTarget,
): Placement | null {
  if (target.kind === "newRow") {
    if (!target.beforeWidgetId) {
      return { toIndex: ordered.length, isInsertionPoint: true, ownRow: true }
    }

    const toIndex = ordered.findIndex(
      (widget) => widget.id === target.beforeWidgetId,
    )
    return toIndex === -1
      ? null
      : { toIndex, isInsertionPoint: true, ownRow: true }
  }

  if (target.kind === "widget") {
    const toIndex = ordered.findIndex(
      (widget) => widget.id === target.overWidgetId,
    )
    if (toIndex === -1) {
      return null
    }

    // A card already as wide as the grid has no room to share a row, so landing
    // on another card takes that card's place and keeps its own row rather than
    // halving them both. Joining a row is still open to it through the gaps
    // between that row's cards, where sharing is what was aimed at.
    const active = ordered.find((widget) => widget.id === activeId)
    if (active && getRequestedWidgetWidth(active) >= DASHBOARD_GRID_COLUMNS) {
      return { toIndex, isInsertionPoint: false, ownRow: true }
    }

    const overRow = rowContaining(rows, target.overWidgetId)
    return {
      toIndex,
      isInsertionPoint: false,
      ...(target.rebalanceRows && overRow ? { joinRow: overRow } : {}),
    }
  }

  const row = rowContaining(rows, target.rowAnchorId)
  if (!row) {
    return null
  }

  const firstMember = row.widgets[0]?.widget.id
  const rowStart = ordered.findIndex((widget) => widget.id === firstMember)
  if (rowStart === -1) {
    return null
  }

  const isMember = rowMemberIds(row).includes(activeId)
  if (!isMember && row.widgets.length >= MAX_ROW_WIDGETS) {
    return null
  }

  return {
    toIndex: rowStart + Math.min(target.slotIndex, row.widgets.length),
    isInsertionPoint: true,
    joinRow: row,
  }
}

/** An insertion point a dragged widget can be dropped on. */
interface DropSlot {
  /** Row it belongs to, counting from the top of the dashboard. */
  rowIndex: number
  /** Position within that row: 0 is before the first card. */
  slotIndex: number
  /** Column the slot sits on, 0 through DASHBOARD_GRID_COLUMNS. */
  columnOffset: number
  /** A current member of the row, used to find it again after the drop. */
  rowAnchorId: string
}

/**
 * Every insertion point open to the dragged widget: the edges and the gaps
 * between cards of each row it could join. A row already holding the maximum
 * offers none, unless the dragged widget is one of its own — shuffling a card
 * within its row does not grow it.
 */
export function getDropSlots(rows: FlowRow[], activeId: string): DropSlot[] {
  const slots: DropSlot[] = []

  rows.forEach((row, rowIndex) => {
    const memberIds = rowMemberIds(row)
    const isMember = memberIds.includes(activeId)
    const anchorId = memberIds[0]

    if (!anchorId || (!isMember && memberIds.length >= MAX_ROW_WIDGETS)) {
      return
    }

    let columnOffset = 0
    row.widgets.forEach((entry, slotIndex) => {
      slots.push({ rowIndex, slotIndex, columnOffset, rowAnchorId: anchorId })
      columnOffset += entry.columns
    })
    slots.push({
      rowIndex,
      slotIndex: row.widgets.length,
      columnOffset: DASHBOARD_GRID_COLUMNS,
      rowAnchorId: anchorId,
    })
  })

  return slots
}

/** A landing point for a brand new row, on the boundary between two rows. */
interface RowBoundarySlot {
  /** Rows above it: 0 sits above the first row, `rows.length` below the last. */
  boundaryIndex: number
  /** First card of the row below it; absent on the boundary at the bottom. */
  beforeWidgetId?: string
}

/**
 * Every boundary a new row could open on. Boundaries touching a row the
 * dragged widget already has to itself are left out — it is a row of its own
 * there already, so a drop would change nothing.
 */
export function getRowBoundarySlots(
  rows: FlowRow[],
  activeId: string,
): RowBoundarySlot[] {
  const hasOnlyActive = (rowIndex: number) => {
    const row = rows[rowIndex]
    return row?.widgets.length === 1 && row.widgets[0]?.widget.id === activeId
  }

  const slots: RowBoundarySlot[] = []

  for (
    let boundaryIndex = 0;
    boundaryIndex <= rows.length;
    boundaryIndex += 1
  ) {
    if (hasOnlyActive(boundaryIndex - 1) || hasOnlyActive(boundaryIndex)) {
      continue
    }

    const beforeWidgetId = rows[boundaryIndex]?.widgets[0]?.widget.id
    slots.push({
      boundaryIndex,
      ...(beforeWidgetId ? { beforeWidgetId } : {}),
    })
  }

  return slots
}

/**
 * Sets a widget's requested width, and hands the columns it gave up — or took —
 * to the rest of its row, which keeps the row spanning the grid and so keeps the
 * widget's new width exactly as asked. Without that the packer would grow every
 * card in the row to close the gap, and a width set to a third would render as
 * something else; wherever the gap was wide enough it would instead be closed by
 * the next widget in the sequence flowing up into the row.
 *
 * A widget alone in its row has nobody to trade with, so the row it leaves short
 * is still filled from the sequence — which is how a full-width card is opened up
 * to let the next one alongside it.
 */
export function setWidgetWidth(
  widgets: DashboardWidget[],
  widgetId: string,
  columns: number,
): DashboardWidget[] {
  const ordered = sortWidgetsByFlowOrder(widgets)
  const resized = ordered.find((widget) => widget.id === widgetId)
  if (!resized) {
    return widgets
  }

  const width = snapWidgetWidth(resized, columns)
  const row = rowContaining(packDashboardRows(ordered), widgetId)
  const absorbingIds = new Set(
    rowMemberIds(row ?? { widgets: [], rowSpan: 1 }).filter(
      (id) => id !== widgetId,
    ),
  )

  // An even share of what is left over. Each one is snapped in turn, so a widget
  // too wide to take its share holds at its own minimum and the packer moves
  // whatever no longer fits onto the next row.
  const share = absorbingIds.size
    ? Math.floor((DASHBOARD_GRID_COLUMNS - width) / absorbingIds.size)
    : 0

  return applyFlowPositions(
    ordered.map((widget) => {
      if (widget.id === widgetId) {
        return { ...widget, grid_w: width }
      }
      return absorbingIds.has(widget.id)
        ? { ...widget, grid_w: snapWidgetWidth(widget, share) }
        : widget
    }),
  )
}

/**
 * Adds a widget at the end of the sequence, or right after another widget —
 * how a clone lands next to its source, joining that row and re-splitting it.
 */
export function insertWidget(
  widgets: DashboardWidget[],
  widget: DashboardWidget,
  afterWidgetId?: string,
): DashboardWidget[] {
  const sized = { ...widget, grid_w: snapWidgetWidth(widget, widget.grid_w) }
  const ordered = sortWidgetsByFlowOrder(widgets)
  const afterIndex = afterWidgetId
    ? ordered.findIndex((candidate) => candidate.id === afterWidgetId)
    : -1

  if (afterIndex === -1) {
    return applyFlowPositions([...ordered, sized])
  }

  const hostRow = rowContaining(packDashboardRows(ordered), afterWidgetId ?? "")
  const inserted = [
    ...ordered.slice(0, afterIndex + 1),
    sized,
    ...ordered.slice(afterIndex + 1),
  ]

  if (!hostRow) {
    return applyFlowPositions(inserted)
  }

  return applyFlowPositions(
    splitRowEvenly(inserted, [...rowMemberIds(hostRow), sized.id]),
  )
}

/**
 * Removes a widget and re-splits the row it leaves behind between the widgets
 * that remain in it — the last one standing goes full width.
 */
export function removeWidget(
  widgets: DashboardWidget[],
  widgetId: string,
): DashboardWidget[] {
  const ordered = sortWidgetsByFlowOrder(widgets)
  const vacatedRow = rowContaining(packDashboardRows(ordered), widgetId)
  const survivors = ordered.filter((widget) => widget.id !== widgetId)

  if (!vacatedRow) {
    return applyFlowPositions(survivors)
  }

  return applyFlowPositions(
    splitRowEvenly(
      survivors,
      rowMemberIds(vacatedRow).filter((id) => id !== widgetId),
    ),
  )
}
